import { Controller } from "stimulus"
import { addWorkingDays, toDateInput } from "../phone_dashboard/working_days"
import { DueDatePicker, dueDateLabel, openDueDatePicker, parseDateInput, startOfToday } from "../phone_dashboard/due_date_picker"
import { showToast } from "../utils/toast"

interface Candidate {
  id: string
  name: string
  first_name?: string
  title?: string
  company?: string
  campaign?: string
  avatar?: string
  email?: string | null
  phone?: string | null
  linkedin?: string | null
  ai_level?: string | null
  ai_score?: number | string | null
  ai_summary?: string | null
  invite_defaults?: string[]
  sender?: { name: string; email: string } | null
}

export interface InviteAnswer { cancelled?: boolean; invite?: "sent" | "skipped"; briefSent?: boolean }

interface OpenDetail {
  candidate: Candidate
  callNote?: string
  callLogId?: string | null
  recording?: { url: string; label: string } | null
  resolve: (answer: InviteAnswer) => void
}

type ChipKind = "cand" | "def" | "rec" | "add"
interface Chip { email: string; kind: ChipKind }

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const LINKEDIN_ICON = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 110-4.12 2.06 2.06 0 010 4.12zM7.12 20.45H3.56V9h3.56v11.45z"/></svg>'
const PLAY_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M10 8l6 4-6 4z"/></svg>'
const LEVELS: Record<string, string> = { strong: "Strong", good: "Good", possible: "Possible", bad: "Poor" }

// The interview dialog (§5): book the interview, then brief the hiring team.
export default class extends Controller {
  static targets = [
    "heading", "sub", "stepDot", "step1", "step2", "candAvatar", "candName", "candSub", "candMail",
    "defaultsNote", "attendeeChips", "attendeeInput", "noEmail",
    "dateButton", "dateLabel", "date", "time", "length", "lengthDd", "lengthLabel", "title", "description",
    "briefChips", "briefInput", "subject", "body", "links", "senderNote", "error",
    "skip", "back", "next", "send"
  ]
  static values = { url: String }

  declare readonly headingTarget: HTMLElement
  declare readonly subTarget: HTMLElement
  declare readonly stepDotTargets: HTMLElement[]
  declare readonly step1Target: HTMLElement
  declare readonly step2Target: HTMLElement
  declare readonly candAvatarTarget: HTMLElement
  declare readonly candNameTarget: HTMLElement
  declare readonly candSubTarget: HTMLElement
  declare readonly candMailTarget: HTMLElement
  declare readonly defaultsNoteTarget: HTMLElement
  declare readonly attendeeChipsTarget: HTMLElement
  declare readonly attendeeInputTarget: HTMLInputElement
  declare readonly noEmailTarget: HTMLElement
  declare readonly dateButtonTarget: HTMLButtonElement
  declare readonly dateLabelTarget: HTMLElement
  declare readonly dateTarget: HTMLInputElement
  declare readonly timeTarget: HTMLInputElement
  declare readonly lengthTarget: HTMLInputElement
  declare readonly lengthDdTarget: HTMLElement
  declare readonly lengthLabelTarget: HTMLElement
  declare readonly titleTarget: HTMLInputElement
  declare readonly descriptionTarget: HTMLTextAreaElement
  declare readonly briefChipsTarget: HTMLElement
  declare readonly briefInputTarget: HTMLInputElement
  declare readonly subjectTarget: HTMLInputElement
  declare readonly bodyTarget: HTMLTextAreaElement
  declare readonly linksTarget: HTMLElement
  declare readonly senderNoteTarget: HTMLElement
  declare readonly errorTarget: HTMLElement
  declare readonly skipTarget: HTMLButtonElement
  declare readonly backTarget: HTMLButtonElement
  declare readonly nextTarget: HTMLButtonElement
  declare readonly sendTarget: HTMLButtonElement
  declare readonly urlValue: string

  private detail: OpenDetail | null = null
  private attendees: Chip[] = []
  private recipients: Chip[] = []
  private calendar: DueDatePicker | null = null
  private bodyTouched = false
  private sending = false

  open(event: CustomEvent<OpenDetail>): void {
    const detail = event.detail
    if (!detail?.candidate) return

    this.detail = detail
    const c = detail.candidate
    const first = c.first_name || c.name.split(/\s+/)[0] || "there"
    const defaults = (c.invite_defaults || []).map(e => e.toLowerCase())

    this.attendees = []
    if (c.email) this.attendees.push({ email: c.email.toLowerCase(), kind: "cand" })
    defaults.forEach(email => this.attendees.push({ email, kind: "def" }))
    if (c.sender?.email) this.attendees.push({ email: c.sender.email.toLowerCase(), kind: "rec" })
    this.attendees = this.dedupe(this.attendees)
    this.recipients = defaults.map(email => ({ email, kind: "def" as ChipKind }))

    this.renderCandidate(c, defaults.length)
    this.noEmailTarget.hidden = !!c.email
    this.noEmailTarget.textContent = `No email on file for ${c.name}. Add their address, or the invite only reaches the hiring team.`

    this.setDate(toDateInput(addWorkingDays(new Date(), 3)))
    this.timeTarget.value = "10:00"
    this.setLength("30")
    this.titleTarget.value = `Intro interview: ${first} × ${c.campaign || "the role"}`
    this.descriptionTarget.value =
      `Hi ${first}, thanks for the call today. Here is the intro interview we talked about for the ` +
      `${c.campaign || "open"} role. If the time doesn't work, reply and we'll find another slot.`

    this.subjectTarget.value = `Candidate brief: ${c.name} – ${c.campaign || ""}`.trim()
    this.bodyTouched = false
    this.renderLinks()
    this.senderNoteTarget.hidden = !!c.sender
    this.senderNoteTarget.textContent = "Connect Gmail, Outlook or Nylas under Profile to send invites."
    this.sendTarget.disabled = !c.sender

    this.sending = false
    this.clearError()
    this.renderChips()
    this.showStep(1)
    this.element.classList.add("open")
    this.element.setAttribute("aria-hidden", "false")
    this.attendeeInputTarget.focus()
  }

  close(): void {
    if (this.sending) return
    this.finish({ cancelled: true })
  }

  closeOnBackdrop(event: Event): void {
    if (event.target === this.element) this.close()
  }

  // Esc closes the calendar or the Length menu first, then the dialog.
  keydown(event: KeyboardEvent): void {
    if (event.key !== "Escape" || !this.isOpen()) return
    if (this.calendar?.isOpen()) {
      this.calendar.hide()
      return
    }
    if (this.lengthDdTarget.classList.contains("open")) {
      this.closeLength()
      return
    }
    this.close()
  }

  toggleLength(event: Event): void {
    event.stopPropagation()
    if (this.lengthDdTarget.classList.contains("open")) {
      this.closeLength()
      return
    }
    this.lengthDdTarget.classList.add("open")
    this.lengthDdTarget.querySelector(".dd-trigger")?.setAttribute("aria-expanded", "true")
    document.addEventListener("click", this.onOutsideLength)
  }

  pickLength(event: Event): void {
    const value = (event.currentTarget as HTMLElement).dataset.value || "30"
    this.setLength(value)
    this.closeLength()
  }

  skip(): void {
    this.finish({ invite: "skipped" })
  }

  next(): void {
    this.commitInput(this.attendeeInputTarget, "attendees")
    const problem = this.stepOneProblem()
    if (problem) return this.showError(problem)

    this.clearError()
    if (!this.bodyTouched) this.bodyTarget.value = this.briefBody()
    this.showStep(2)
    this.subjectTarget.focus()
  }

  back(): void {
    this.clearError()
    this.showStep(1)
  }

  bodyEdited(): void {
    this.bodyTouched = true
  }

  pickDate(event: Event): void {
    event.stopPropagation()
    if (this.calendar?.isOpen()) {
      this.calendar.hide()
      return
    }
    this.calendar = openDueDatePicker(this.dateTarget, this.dateButtonTarget, date => {
      this.setDate(date)
      this.clearError()
    })
  }

  chipKey(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement
    const list = input === this.briefInputTarget ? "recipients" : "attendees"
    if (event.key === "Enter" || event.key === "," || event.key === " ") {
      event.preventDefault()
      this.commitInput(input, list)
    } else if (event.key === "Backspace" && !input.value) {
      const chips = this[list]
      if (chips.length) {
        chips.pop()
        this.renderChips()
      }
    }
  }

  chipBlur(event: Event): void {
    const input = event.target as HTMLInputElement
    this.commitInput(input, input === this.briefInputTarget ? "recipients" : "attendees")
  }

  async send(): Promise<void> {
    if (this.sending || !this.detail) return
    this.commitInput(this.briefInputTarget, "recipients")
    const problem = this.stepOneProblem() || (this.recipients.length ? "" : "Add at least one recipient for the brief.")
    if (problem) return this.showError(problem)

    this.setSending(true)
    try {
      const response = await fetch(this.urlValue.replace(":id", encodeURIComponent(this.detail.candidate.id)), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          attendees: this.attendees.map(chip => chip.email),
          starts_at: this.startsAt()?.toISOString(),
          duration_min: Number(this.lengthTarget.value),
          title: this.titleTarget.value,
          description: this.descriptionTarget.value,
          call_log_id: this.detail.callLogId || null,
          summary: { to: this.recipients.map(chip => chip.email), subject: this.subjectTarget.value, body: this.bodyTarget.value }
        })
      })
      const data = await response.json().catch(() => null)
      this.setSending(false)
      if (!response.ok || !data?.ok) {
        this.showError((data?.errors || [])[0] || "The invite wasn't sent. Try again.")
        return
      }
      if (!data.brief_sent) showToast("Invite sent; the brief could not be sent", "error", 3500)
      this.finish({ invite: "sent", briefSent: !!data.brief_sent })
    } catch (_e) {
      this.setSending(false)
      this.showError("That didn't reach the server. Nothing was sent — try again.")
    }
  }

  // --- internals -----------------------------------------------------------

  private readonly onOutsideLength = (event: Event): void => {
    if (!this.lengthDdTarget.contains(event.target as Node)) this.closeLength()
  }

  private closeLength(): void {
    this.lengthDdTarget.classList.remove("open")
    this.lengthDdTarget.querySelector(".dd-trigger")?.setAttribute("aria-expanded", "false")
    document.removeEventListener("click", this.onOutsideLength)
  }

  private setLength(value: string): void {
    this.lengthTarget.value = value
    this.lengthLabelTarget.textContent = `${value} min`
    this.lengthDdTarget.querySelectorAll<HTMLElement>(".dd-opt").forEach(opt => {
      const on = opt.dataset.value === value
      opt.classList.toggle("active", on)
      opt.setAttribute("aria-selected", String(on))
    })
  }

  private finish(answer: InviteAnswer): void {
    this.closeLength()
    this.calendar?.hide()
    this.element.classList.remove("open")
    this.element.setAttribute("aria-hidden", "true")
    const resolve = this.detail?.resolve
    this.detail = null
    if (resolve) resolve(answer)
  }

  private showStep(step: 1 | 2): void {
    this.step1Target.hidden = step !== 1
    this.step2Target.hidden = step !== 2
    this.skipTarget.hidden = step !== 1
    this.nextTarget.hidden = step !== 1
    this.backTarget.hidden = step !== 2
    this.sendTarget.hidden = step !== 2
    this.stepDotTargets.forEach((dot, i) => {
      dot.classList.toggle("on", i === step - 1)
      dot.classList.toggle("done", i < step - 1)
    })
    this.headingTarget.textContent = step === 1 ? "Book the interview" : "Brief the hiring team"
    this.subTarget.textContent = step === 1
      ? "Step 1 · Calendar invite to the candidate and the hiring team."
      : "Step 2 · Summary email with the call details, sent to the hiring team only."
  }

  // The candidate strip above both steps: avatar, name, title · campaign, and the email the invite goes to.
  private renderCandidate(c: Candidate, defaults: number): void {
    this.candAvatarTarget.innerHTML = c.avatar || ""
    this.candNameTarget.textContent = c.name
    this.candSubTarget.textContent = [c.title, c.campaign].filter(Boolean).join(" · ")
    this.candMailTarget.hidden = !c.email
    this.candMailTarget.innerHTML = ""
    const dot = document.createElement("span")
    dot.className = "pdot"
    this.candMailTarget.append(dot, c.email || "")
    const campaign = c.campaign || "this campaign"
    this.defaultsNoteTarget.textContent = defaults
      ? `· candidate + ${defaults} default from ${campaign}`
      : `· candidate only, no defaults set for ${campaign}`
  }

  private stepOneProblem(): string {
    if (!this.attendees.length) return "Add at least one attendee."
    const start = this.startsAt()
    if (!start) return "Pick a date and time."
    if (start.getTime() <= Date.now()) return "Pick a time later than now."
    if (!this.titleTarget.value.trim()) return "Give the interview a title."
    return ""
  }

  private startsAt(): Date | null {
    const date = parseDateInput(this.dateTarget.value)
    const [hours, minutes] = (this.timeTarget.value || "").split(":").map(Number)
    if (!date || Number.isNaN(hours) || Number.isNaN(minutes)) return null
    date.setHours(hours, minutes, 0, 0)
    return date
  }

  private setDate(value: string): void {
    this.dateTarget.value = value
    this.dateLabelTarget.textContent = dueDateLabel(value)
    const date = parseDateInput(value)
    if (date && date < startOfToday()) this.dateTarget.value = toDateInput(startOfToday())
  }

  private commitInput(input: HTMLInputElement, list: "attendees" | "recipients"): void {
    const values = input.value.split(/[,\s]+/).map(v => v.trim().toLowerCase()).filter(Boolean)
    if (!values.length) return

    const bad = values.filter(v => !EMAIL.test(v))
    if (bad.length) {
      this.showError(`${bad.join(", ")} is not a valid email address`)
      return
    }
    const candidate = this.detail?.candidate.email?.toLowerCase()
    values.forEach(email => {
      if (list === "recipients" && email === candidate) {
        this.showError("The candidate can't be on the brief.")
        return
      }
      this[list].push({ email, kind: "add" })
    })
    this[list] = this.dedupe(this[list])
    input.value = ""
    this.renderChips()
  }

  private dedupe(chips: Chip[]): Chip[] {
    const seen = new Set<string>()
    return chips.filter(chip => (seen.has(chip.email) ? false : (seen.add(chip.email), true)))
  }

  private renderChips(): void {
    this.paint(this.attendeeChipsTarget, this.attendeeInputTarget, this.attendees, "attendees")
    this.paint(this.briefChipsTarget, this.briefInputTarget, this.recipients, "recipients")
  }

  private paint(box: HTMLElement, input: HTMLInputElement, chips: Chip[], list: "attendees" | "recipients"): void {
    box.querySelectorAll(".iv-chip").forEach(el => el.remove())
    chips.forEach((chip, index) => {
      const el = document.createElement("span")
      // prefixed: a bare `.cand` is the design system's candidate column
      el.className = `iv-chip is-${chip.kind}`
      const text = document.createElement("span")
      text.className = "iv-chip-text"
      text.textContent = chip.email
      el.appendChild(text)
      const remove = document.createElement("button")
      remove.type = "button"
      remove.title = "Remove"
      remove.setAttribute("aria-label", `Remove ${chip.email}`)
      remove.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>'
      remove.addEventListener("click", () => {
        this[list].splice(index, 1)
        this.renderChips()
      })
      el.appendChild(remove)
      box.insertBefore(el, input)
    })
  }

  private renderLinks(): void {
    const c = this.detail?.candidate
    const parts: string[] = []
    this.linksTarget.innerHTML = ""
    if (c?.linkedin) parts.push(`<a href="${encodeURI(c.linkedin)}" target="_blank" rel="noopener">${LINKEDIN_ICON}LinkedIn profile</a>`)
    const rec = this.detail?.recording
    if (rec) parts.push(`<a href="${encodeURI(rec.url)}" target="_blank" rel="noopener">${PLAY_ICON}${rec.label.replace(/[<>&]/g, "")}</a>`)
    this.linksTarget.innerHTML = parts.join("")
    this.linksTarget.hidden = !parts.length
  }

  // The brief, prefilled from the card and step 1 (§5 step 2).
  private briefBody(): string {
    const d = this.detail
    if (!d) return ""
    const c = d.candidate
    const start = this.startsAt()
    const when = start
      ? `${dueDateLabel(this.dateTarget.value)}, ${this.timeTarget.value} (${this.lengthTarget.value} min)`
      : ""
    const level = c.ai_level && LEVELS[c.ai_level] ? `${LEVELS[c.ai_level]}${c.ai_score != null ? ` · ${c.ai_score}` : ""}` : ""
    const lines = [
      `Interview booked: ${when}`,
      "",
      `${c.name}${c.title ? ` — ${c.title}` : ""}${c.company && !(c.title || "").includes(c.company) ? ` at ${c.company}` : ""}`,
      level ? `AI fit: ${level}` : null,
      c.phone && `Phone: ${c.phone}`,
      c.email && `Email: ${c.email}`,
      c.linkedin && `LinkedIn: ${c.linkedin}`
    ]
    if (c.ai_summary) lines.push("", "AI summary:", c.ai_summary)
    if (d.callNote) lines.push("", "Call notes:", d.callNote)
    if (d.recording) lines.push("", `${d.recording.label}: ${d.recording.url}`)
    return lines.filter((line): line is string => typeof line === "string").join("\n")
  }

  private setSending(sending: boolean): void {
    this.sending = sending
    this.sendTarget.disabled = sending || !this.detail?.candidate.sender
    this.backTarget.disabled = sending
  }

  private showError(message: string): void {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  private clearError(): void {
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }

  private isOpen(): boolean {
    return this.element.classList.contains("open")
  }
}
